"use client";

import React, { useEffect, useState } from "react";
import { apiPost } from "@/utils/apiClient";
import { Scheme } from "@/type/schemeType";
import { ROOT_URL } from "@/utils/gloableVariables";

type FundMetaItem = {
  label: string;
  href?: string;
  isStatus?: boolean;
  statusColor?: string;
  isRecommended?: boolean;
  redirect?: number;
};

type RecommendedApiResponse = {
  status: number;
  message: string;
  url?: string;
  redirect?: number;
};

export default function FundSubList({ fund }: { fund: Scheme }) {
  const [recommendedItem, setRecommendedItem] = useState<FundMetaItem | null>(null);

  const fundMetaList: FundMetaItem[] = [
    {
      label: fund.category_type || "",
      href: `${ROOT_URL}mutual-funds/${fund.category_type?.toLowerCase()}`,
    },
    {
      label: fund.cat_name || "",
      href: `${ROOT_URL}mutual-funds/${fund.cat_url}`,
    },
    {
      label: `Status: ${fund.sip_allowed || fund.lump_allowed ? "Open" : "Closed"}`,
      isStatus: true,
      statusColor: fund.sip_allowed || fund.lump_allowed ? "#229e3d" : "#ec4d4d",
    },
  ];

  useEffect(() => {
    async function fetchRecommendation() {
      try {
        const res: RecommendedApiResponse = await apiPost("isSchemeRecommended", { isin: fund.u_code });

        if (res.status === 1) {
          setRecommendedItem({
            label: "Recommended",
            isRecommended: true,
            href: res.redirect === 1 ? res.url : undefined,
            redirect: res.redirect,
          });
        }
      } catch (err) {
        console.error("Recommendation API error:", err);
      }
    }

    if (fund?.u_code) {
      fetchRecommendation();
    }
  }, [fund?.u_code]);

  if (recommendedItem) {
    fundMetaList.push(recommendedItem);
  }

  return (
    <ul className="flex items-center justify-start gap-2 mt-3 sm:mt-2 h-6.5 sm:h-7.5">
      {fundMetaList.map((item, idx) => (
        <li
          key={idx}
          className={`text-xs sm:text-base inline-flex items-center justify-center gap-2 after:content-[''] after:inline-block after:w-1.5 after:h-1.5 after:rounded-full after:bg-[#6A82A8] after:shrink-0 ${idx === fundMetaList.length - 1 ? "after:hidden" : ""} ${item.isStatus || item.isRecommended ? "" : "text-[#6A82A8]"}`}
          style={item.isStatus ? { color: item.statusColor } : {}}
        >
          {item.href && !item.isRecommended ? (
            <a href={item.href} className="line-clamp-1">
              {item.label}
            </a>
          ) : item.isStatus ? (
            <>
              <span className="hidden xs:inline text-[#6A82A8] whitespace-nowrap">Status:</span>{" "}
              {fund.sip_allowed || fund.lump_allowed ? "Open" : "Closed"}
            </>
          ) : item.isRecommended ? (
            <>
              {item.redirect === 1 && item.href ? (
                <a
                  href={item.href}
                  className="inline-flex items-center gap-2 px-1.5 sm:px-3 py-1 rounded-full border border-primaryBlue text-primaryBlue text-xs sm:text-sm font-medium transition duration-350 hover:bg-[#EBF3FF] hover:text-primaryBlue"
                >
                  <RecommendedIcon />
                  {item.label}
                </a>
              ) : (
                <div className="inline-flex items-center gap-2 px-1.5 sm:px-3 py-1 rounded-full border border-primaryBlue text-primaryBlue text-xs sm:text-sm font-medium">
                  <RecommendedIcon />
                  {item.label}
                </div>
              )}
            </>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function RecommendedIcon() {
  return (
    <svg width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2.33736 7.7155C2.33736 7.7155 0.345746 10.2729 1.38897 11.1254C1.38897 11.1254 1.76833 11.3149 2.62188 11.2201C5.3722 10.8413 12.2007 5.82107 14.5717 3.73723L14.1923 2.69531L17.891 3.26363L16.6581 6.95776L15.8994 5.91579C15.8994 5.91579 8.88125 11.599 3.28575 12.925C3.28575 12.925 -3.35297 14.2512 2.24252 7.7155H2.33736Z"
        fill="#1A51AA"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2.91016 5.49219H5.82013V9.45056C4.9753 10.016 3.94272 10.393 2.91016 10.77V5.49219Z"
        fill="#1A51AA"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.79004 2.69531H9.70002V6.95474L6.79004 8.94255V2.69531Z"
        fill="#1A51AA"
      />
      <path fillRule="evenodd" clipRule="evenodd" d="M10.6699 0H13.5799V4.07855L10.6699 6.35494V0Z" fill="#1A51AA" />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M13.472 13.9977H10.562V10.192L13.472 8.28906V13.9977Z"
        fill="#1A51AA"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9.70002 13.9953H6.79004V12.1826L9.70002 10.6562V13.9953Z"
        fill="#1A51AA"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.82013 13.994H2.91016V13.4339C3.56725 13.4339 4.9753 12.9672 5.82013 12.5938V13.994Z"
        fill="#1A51AA"
      />
    </svg>
  );
}
